import './DnDImgPicker.scss';

import classNames from 'classnames';
import React, {
  ChangeEvent, DragEvent, useCallback,
  useEffect,
  useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

interface IDnDFilePickerProps {
  img: Blob | undefined;
  onSetImg: React.Dispatch<React.SetStateAction<Blob | undefined>>;
}

const DnDFilePicker: React.FC<IDnDFilePickerProps> = ({ img, onSetImg }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [highlight, setHighlight] = useState(false);

  useEffect(() => {
    setErrorMsg('');
  }, [img]);

  const changeFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target && e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      handleFile(file);
    }
  };

  const dragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setHighlight(true);
  };

  const dragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setHighlight(false);
  };

  const drop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setHighlight(false);
    const draggedData = e.dataTransfer;
    if (draggedData && draggedData.files && draggedData.files.length > 0) {
      const file = draggedData.files[0];
      handleFile(file);
    }
  };

  const handleFile = (file: Blob) => {
    const isValidImageFile = ['image/jpeg', 'image/png'].includes(file.type);
    if (!isValidImageFile) {
      setErrorMsg('Please upload an image file');
    } else {
      setErrorMsg('');
      onSetImg(file);
    }
  };

  const selectFile = useCallback(() => {
    inputRef.current?.click();
  }, []);

  return (
    <div
      className={classNames(highlight && 'dnd-img-picker__highlight', 'dnd-img-picker position-relative')}
      onDragEnter={dragEnter}
      onDragLeave={dragLeave}
      onDragOver={dragEnter}
      onDrop={drop}
    >
      <input
        type="file"
        accept="image/jpeg, image/png"
        onChange={changeFile}
        ref={inputRef}
      />
      <button type="button" className="btn btn-dark w-60" onClick={selectFile}>
        <i className="icon icon-upload me-8" />
        {t('upload.img.modal.choose.btn')}
      </button>
      {errorMsg
        && (
          <div className="text-secondary mt-12">
            {errorMsg}
          </div>
        )}
    </div>
  );
};

export default DnDFilePicker;
